import type { AxiosInstance } from "axios";
import { Check, Inbox, Loader2, TriangleAlert, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { apiErrorMessage } from "../lib/api.js";
import {
  approveJoinRequest,
  fetchAgencyRequests,
  fetchStoreJoinStatus,
  formatRequestDate,
  planDescription,
  rejectJoinRequest,
  requestJoinForStore,
  type AgencyRequests,
  type JoinRequest,
  type StoreJoinStatus,
} from "../lib/join-requests.js";
import { AgencyJoinForm } from "./AgencyJoinForm.js";
import { Badge } from "./ui/badge.js";
import { Button } from "./ui/button.js";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card.js";
import { Input } from "./ui/input.js";
import { Label } from "./ui/label.js";

/**
 * CLIENT REQUESTS (Flow 1, agency side): stores whose merchants installed Ecomally from the App Store
 * and named this agency. Each waits for an owner or admin here to approve — the store then joins as
 * a new client, with its data — or reject. Shown only to an agency's owner/admin; the email's link
 * lands on `#client-requests`.
 */
export function ClientRequests({ client }: { readonly client: AxiosInstance }): React.JSX.Element | null {
  const [data, setData] = useState<AgencyRequests | null>(null);
  const [error, setError] = useState<string | null>(null);
  const anchor = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      setData(await fetchAgencyRequests(client));
    } catch (caught) {
      setError(apiErrorMessage(caught, "Could not load client requests."));
    }
  }, [client]);

  useEffect(() => {
    void load();
  }, [load]);

  // Arriving from the email: bring the section into view once it has rendered.
  useEffect(() => {
    if (data?.available === true && window.location.hash === "#client-requests") anchor.current?.scrollIntoView({ block: "start" });
  }, [data]);

  if (data === null || !data.available) return error === null ? null : <p className="text-destructive text-sm">{error}</p>;

  const pending = data.requests.filter((r) => r.state === "pending");
  const decided = data.requests.filter((r) => r.state !== "pending").slice(0, 10);

  return (
    <div id="client-requests" ref={anchor} className="scroll-mt-6">
      <Card className="gap-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Inbox className="text-primary size-5" />
            Client requests
            {pending.length > 0 && <Badge className="ml-1">{pending.length} waiting</Badge>}
          </CardTitle>
          <CardDescription>
            Stores that installed Ecomally themselves and asked to join your agency. Approving adds the store as a new client,
            with its data; its own team keeps access to that store only.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {pending.length === 0 ? (
            <p className="text-muted-foreground text-sm">No requests waiting.</p>
          ) : (
            pending.map((request) => <PendingRequest key={request.id} request={request} client={client} onDecided={() => void load()} />)
          )}
          {decided.length > 0 && (
            <ul className="text-muted-foreground flex flex-col gap-1 border-t pt-3 text-xs">
              {decided.map((request) => (
                <li key={request.id}>
                  <span className="text-foreground font-medium">{request.store.displayName}</span> ({request.store.shopDomain}) —{" "}
                  {request.state === "approved" ? "approved" : request.state === "rejected" ? "rejected" : "expired"}
                  {request.decidedBy !== null ? ` by ${request.decidedBy}` : ""}
                  {request.decidedAt !== null ? `, ${formatRequestDate(request.decidedAt)}` : ""}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function PendingRequest({
  request,
  client,
  onDecided,
}: {
  readonly request: JoinRequest;
  readonly client: AxiosInstance;
  readonly onDecided: () => void;
}): React.JSX.Element {
  const [name, setName] = useState(request.store.displayName);
  const [busy, setBusy] = useState<"approve" | "reject" | null>(null);
  const [confirmReject, setConfirmReject] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const decide = async (decision: "approve" | "reject"): Promise<void> => {
    setBusy(decision);
    setError(null);
    try {
      if (decision === "approve") await approveJoinRequest(client, request.id, name.trim());
      else await rejectJoinRequest(client, request.id);
      onDecided();
    } catch (caught) {
      setError(apiErrorMessage(caught, decision === "approve" ? "Could not approve the request." : "Could not reject the request."));
      setBusy(null);
    }
  };

  return (
    <div data-testid="pending-request" className="flex flex-col gap-3 rounded-lg border px-4 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <p className="text-sm">
          <span className="font-medium">{request.store.displayName}</span>{" "}
          <span className="text-muted-foreground">· {request.store.shopDomain}</span>
        </p>
        <p className="text-muted-foreground text-xs">
          Asked {formatRequestDate(request.createdAt)} · expires {formatRequestDate(request.expiresAt)}
        </p>
      </div>
      {request.oldPlan !== null && (
        <p
          data-testid="old-plan-warning"
          className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100"
        >
          <TriangleAlert aria-hidden className="mt-0.5 size-4 shrink-0 text-amber-600" />
          <span>
            This store's own account has a paid plan ({planDescription(request.oldPlan)}). Adding it to your agency does not cancel
            that plan — ask the merchant to cancel it, or they will keep paying for it.
          </span>
        </p>
      )}
      <div className="flex flex-wrap items-end gap-2">
        <div className="grid min-w-48 flex-1 gap-1.5">
          <Label htmlFor={`client-name-${request.id}`} className="text-xs">
            Client name
          </Label>
          <Input id={`client-name-${request.id}`} value={name} maxLength={100} onChange={(e) => setName(e.target.value)} disabled={busy !== null} />
        </div>
        {confirmReject ? (
          <>
            <Button variant="destructive" disabled={busy !== null} onClick={() => void decide("reject")}>
              {busy === "reject" ? <Loader2 className="animate-spin" /> : <X />}
              Reject request
            </Button>
            <Button variant="ghost" disabled={busy !== null} onClick={() => setConfirmReject(false)}>
              Keep
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" className="text-destructive hover:text-destructive" disabled={busy !== null} onClick={() => setConfirmReject(true)}>
              Reject…
            </Button>
            <Button disabled={busy !== null} onClick={() => void decide("approve")}>
              {busy === "approve" ? <Loader2 className="animate-spin" /> : <Check />}
              Approve &amp; add
            </Button>
          </>
        )}
      </div>
      {error !== null && (
        <p className="text-destructive text-sm" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * The merchant's side on Connect a store: "Managed by an agency?" — offered to a brand's owner/admin
 * for the open store, with where their last request stands.
 */
export function AgencyJoinCard({ client }: { readonly client: AxiosInstance }): React.JSX.Element | null {
  const [status, setStatus] = useState<StoreJoinStatus | null>(null);

  useEffect(() => {
    void fetchStoreJoinStatus(client)
      .then(setStatus)
      .catch((error: unknown) => {
        // Fully handled: without a status the card is simply not offered.
        void error;
      });
  }, [client]);

  if (status === null || !status.canRequest) return null;
  const pending = status.latest?.state === "pending" ? status.latest : null;

  return (
    <Card className="gap-4">
      <CardHeader>
        <CardTitle className="text-base">Managed by an agency?</CardTitle>
        <CardDescription>
          If an agency runs your marketing on Ecomally, ask them to add this store to their account. They see its reports; you
          keep seeing yours.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {pending !== null ? (
          <p role="status" className="text-sm">
            Waiting for <span className="font-medium">{pending.requestedEmail}</span> to approve — asked{" "}
            {formatRequestDate(pending.createdAt)}, expires {formatRequestDate(pending.expiresAt)}.
          </p>
        ) : (
          <AgencyJoinForm idPrefix="dashboard" onSubmit={async (email) => await requestJoinForStore(client, email)} />
        )}
      </CardContent>
    </Card>
  );
}
